import { Card, CardContent, Grid, Stack, TextField, Typography } from '@mui/material'
import { useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useExecutiveDashboard } from '@/api/hooks'
import { useAuthStore } from '@/auth/authStore'
import {
  ErrorBanner,
  formatDate,
  formatMoney,
  formatNumber,
  InlineSpinner,
  isoDate,
  MetricCard,
  PageHeader,
} from '@/components/common'
import { useChartTokens } from '@/theme'



export function ExecutiveDashboardPage() {
  const chart = useChartTokens()
  // Opens on today. Every list and every report starts on the day being
  // worked, and the range is widened when somebody wants history.
  const [from, setFrom] = useState(() => isoDate(0))
  const [to, setTo] = useState(() => isoDate(0))
  // Which branch, if any, is chosen in the toolbar and applies to every screen.
  const outletId = useAuthStore((state) => state.activeOutletId)
  const { data, isLoading, error } = useExecutiveDashboard(from, to)

  return (
    <>
      <PageHeader
        title="Executive dashboard"
        subtitle={
          outletId
            ? 'Enquiry funnel, revenue, collection and satisfaction for one outlet'
            : 'Enquiry funnel, revenue, collection and satisfaction across every outlet'
        }
        actions={
          <Stack direction="row" spacing={1}>
            <TextField
              label="From"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        }
      />

      <ErrorBanner error={error} />
      {isLoading && <InlineSpinner />}

      {data && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Enquiries today" value={formatNumber(data.enquiriesToday)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="Enquiries in period"
                value={formatNumber(data.enquiriesThisMonth)}
                caption={`${formatDate(data.periodFrom)} – ${formatDate(data.periodTo)}`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="Conversion"
                value={`${data.conversionPercent.toFixed(1)}%`}
                tone={data.conversionPercent >= 25 ? 'success' : 'warning'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="Hall occupancy"
                value={`${data.hallOccupancyPercent.toFixed(1)}%`}
                caption="Confirmed hours against capacity"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Confirmed revenue" value={formatMoney(data.confirmedRevenue)} tone="success" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="Forecast revenue"
                value={formatMoney(data.forecastRevenue)}
                caption="Open pipeline weighted by probability"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Collected" value={formatMoney(data.collectedAmount)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="Outstanding"
                value={formatMoney(data.outstandingAmount)}
                tone={data.outstandingAmount > 0 ? 'warning' : 'default'}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Average order value" value={formatMoney(data.averageOrderValue)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard label="Lost enquiry value" value={formatMoney(data.lostEnquiryValue)} tone="error" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="Customer satisfaction"
                value={data.customerSatisfaction ? `${data.customerSatisfaction.toFixed(2)} / 5` : '—'}
                tone={
                  data.customerSatisfaction && data.customerSatisfaction < 3.5 ? 'warning' : 'success'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                label="Repeat customers"
                value={`${data.repeatCustomerPercent.toFixed(1)}%`}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={8}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Revenue by event date
                  </Typography>
                  {data.revenueTrend.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No confirmed events in this period.
                    </Typography>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={data.revenueTrend}>
                        <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(value) => formatDate(value)}
                          fontSize={12}
                          stroke={chart.axis}
                        />
                        <YAxis
                          tickFormatter={(value) => `${Math.round(value / 1000)}k`}
                          fontSize={12}
                          stroke={chart.axis}
                        />
                        <Tooltip
                          formatter={(value: number) => formatMoney(value)}
                          labelFormatter={(label) => formatDate(String(label))}
                          contentStyle={chart.tooltip}
                          labelStyle={{ color: chart.tooltip.color }}
                        />
                        <Line
                          type="monotone"
                          dataKey="amount"
                          name="Revenue"
                          stroke={chart.series[0]}
                          strokeWidth={2}
                          dot={{ r: 3 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Events by service type
                  </Typography>
                  {data.eventsByServiceType.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Nothing booked yet.
                    </Typography>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={data.eventsByServiceType}
                          dataKey="amount"
                          nameKey="name"
                          innerRadius={60}
                          outerRadius={95}
                          paddingAngle={2}
                        >
                          {data.eventsByServiceType.map((entry, index) => (
                            <Cell key={entry.name} fill={chart.series[index % chart.series.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value: number) => formatMoney(value)}
                          contentStyle={chart.tooltip}
                          labelStyle={{ color: chart.tooltip.color }}
                        />
                        <Legend verticalAlign="bottom" height={36} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Revenue by outlet
                  </Typography>
                  {data.eventsByOutlet.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No outlet has confirmed revenue in this period.
                    </Typography>
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={data.eventsByOutlet}>
                        <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                        <XAxis dataKey="name" fontSize={12} stroke={chart.axis} />
                        <YAxis
                          tickFormatter={(value) => `${Math.round(value / 1000)}k`}
                          fontSize={12}
                          stroke={chart.axis}
                        />
                        <Tooltip
                          formatter={(value: number) => formatMoney(value)}
                          contentStyle={chart.tooltip}
                          labelStyle={{ color: chart.tooltip.color }}
                          cursor={{ fill: chart.grid }}
                        />
                        <Bar dataKey="amount" name="Revenue" fill={chart.series[0]} radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </>
  )
}
